import ErrorBoundary from '@/components/error-boundary';
import { AppIntlErrorShell } from '@/components/error-boundary/app-intl-error-shell';
import { AppHead } from '@/components/utils/app-head';
import { BodyFontRefreshMarker } from '@/components/utils/body-font-refresh-marker';
import { SafeTeamMembershipSync } from '@/components/utils/safe-team-membership-sync';
import { KindeBrowserClientProvider } from '@/context/kinde-browser-client-context';
import { FEATURE_FLAG_DOMAIN } from '@/features/flags/constants';
import { OpenFeatureContextSync } from '@/features/flags/openfeature-context-sync';
import { isUserQueryBlockedRoute } from '@/hooks/query/useQueryUser';
import {
  syncPersistedAuthToStore,
  useAuthTokenStorageSync,
} from '@/hooks/useAuthTokenStorageSync';
import { HeidiIntlProvider } from '@/providers/intl-provider';
import { Toaster } from '@heidi/design-system/toast';
import { TooltipProvider } from '@heidi/design-system/tooltip';
import { OpenFeatureProvider } from '@openfeature/react-sdk';
import { getPersistor } from '@rematch/persist';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ThemeProvider } from 'next-themes';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/lib/integration/react';

import { themeProviderConfig } from '@/config/theme-config';
import { store } from '@/store';
import {
  allura,
  dancingScript,
  Exposure,
  fontVariableClasses,
  generalSans,
  greatVibes,
  Inter,
  notoSans,
  notoSerif,
  pacifico,
  satisfy,
  vazirmatn,
} from '@/styles/fonts';
import { isTauri } from '@/tauri/utils';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { onPersistRestored, persistOptions } from '@/utils/reactQueryPersistConfig';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { GeistSans } from 'geist/font/sans';
import { LazyMotion } from 'motion/react';
import { useRouter } from 'next/router';
import { type PropsWithChildren } from 'react';

const persistor = getPersistor();

const isNonProductionEnvironment = getEnvironment() !== ENV_NAME.PROD;

/**
 * Only sync OpenFeature context on routes where user data can be fetched.
 * Haiku pre-auth launch routes have no authenticated user yet.
 */
function SafeOpenFeatureContextSync() {
  const { pathname } = useRouter();
  if (isUserQueryBlockedRoute(pathname)) return null;
  return <OpenFeatureContextSync />;
}

interface MinimalProvidersProps {
  children: React.ReactNode;
  showToasters?: boolean;
}

/**
 * The hosted onboarding pages open mid-login with no session, so the Kinde
 * browser client (which fetches `/api/auth/setup` on mount) is skipped there
 * and nowhere else. `useKindeBrowserClientContext` throws on the web if
 * something on those pages ever needs Kinde.
 */
const KINDE_CLIENT_SKIPPED_ROUTES = new Set(['/auth/onboard', '/auth/onboard-complete']);

const PassThrough = ({ children }: PropsWithChildren) => <>{children}</>;

/**
 * Minimal provider wrapper with only core essentials:
 * - Redux state (with persistence)
 * - React Query (with persistence)
 * - Theme provider
 * - Error boundary
 * - Fonts and styling
 *
 * No heavy providers like AuthWrapper, GlobalModalsWrapper, TourProvider, etc.
 *
 * NOTE: The token cache's root effects (scheduler + telemetry
 * subscriber) are deliberately NOT mounted here — Tauri overlay
 * windows must not prewarm the main session. A WEB page served under
 * this wrapper that consumes the atom-backed auth hooks
 * (`useAuth0Session`, `useIsAuthenticated`, `useAuthSessionMetadata`)
 * MUST mount `useAuthTokenCacheScheduler()` in its own page/container
 * — otherwise the atoms stay `'loading'`/null forever and the hooks
 * return permanently-empty live state. See
 * `src/container/integrations/widget-auth/widget-auth-container.tsx`
 * for the reference pattern.
 */
export function MinimalProviders({
  children,
  showToasters = false,
}: MinimalProvidersProps) {
  const { pathname } = useRouter();
  const isAuxiliaryWindowRoute =
    pathname === '/dictation-widget' || pathname === '/appointment-bar';
  const AuthClientBoundary = KINDE_CLIENT_SKIPPED_ROUTES.has(pathname)
    ? PassThrough
    : KindeBrowserClientProvider;
  useAuthTokenStorageSync();

  return (
    <PersistGate persistor={persistor}>
      <Provider store={store}>
        <OpenFeatureProvider domain={FEATURE_FLAG_DOMAIN}>
          <PersistQueryClientProvider
            client={reactQueryClient}
            persistOptions={persistOptions}
            onSuccess={() => {
              onPersistRestored();
              if (isTauri()) syncPersistedAuthToStore();
            }}
          >
            <ThemeProvider {...themeProviderConfig}>
              <AppIntlErrorShell>
                <HeidiIntlProvider
                  isPseudolocaleFeatureEnabled={isNonProductionEnvironment}
                  syncDocumentHtmlAttributes={!isAuxiliaryWindowRoute}
                >
                  <ErrorBoundary name="minimal_root">
                    <AuthClientBoundary>
                      <TooltipProvider delayDuration={350}>
                        <LazyMotion
                          strict
                          features={() =>
                            import('@/lib/motionFeatures').then((mod) => mod.features)
                          }
                        >
                          <AppHead />
                          <main className={fontVariableClasses}>
                            <style jsx global>{`
                              :root {
                                /* Legacy core — still referenced, not yet retired. */
                                --font-geist-sans: ${GeistSans.style?.fontFamily};
                                /* Current core — Exposure headings; Inter body (General Sans when release-body-font-refresh is on). */
                                --font-exposure: ${Exposure.style?.fontFamily};
                                --font-inter: ${Inter.style?.fontFamily};
                                --font-general-sans: ${generalSans.style?.fontFamily};
                                /* Locale fallbacks — non-Latin scripts (Noto CJK/Cyrillic/Greek, Vazirmatn Arabic). */
                                --font-noto-sans: ${notoSans.style?.fontFamily};
                                --font-noto-serif: ${notoSerif.style?.fontFamily};
                                --font-vazirmatn: ${vazirmatn.style?.fontFamily};
                                /* Signature faces — handwritten fonts for the Defaults footer signature editor. */
                                --font-dancing-script: ${dancingScript.style?.fontFamily};
                                --font-great-vibes: ${greatVibes.style?.fontFamily};
                                --font-allura: ${allura.style?.fontFamily};
                                --font-pacifico: ${pacifico.style?.fontFamily};
                                --font-satisfy: ${satisfy.style?.fontFamily};
                              }
                            `}</style>
                            {/* Must sit under HeidiIntlProvider: OpenFeatureContextSync →
                      useQueryRoles → useTranslationHelpers → useIntl() (SCR-5977).
                      Same order as `_app.tsx`. Outside Intl the overlay tree
                      crashes before CopilotOverlayApp paints. */}
                            <SafeOpenFeatureContextSync />
                            <SafeTeamMembershipSync />
                            <BodyFontRefreshMarker />
                            {children}
                            {showToasters && <Toaster />}
                          </main>
                        </LazyMotion>
                      </TooltipProvider>
                    </AuthClientBoundary>
                  </ErrorBoundary>
                </HeidiIntlProvider>
              </AppIntlErrorShell>
            </ThemeProvider>
          </PersistQueryClientProvider>
        </OpenFeatureProvider>
      </Provider>
    </PersistGate>
  );
}
